import { getAccessToken } from "../../utils/auth";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { IconButton } from "@mui/material";
import * as XLSX from "xlsx";
import axios from "../../utils/api.js";

const CelebrityHomeDetails = () => {
  const [celebrityhomes, setCelebrityHome] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCelebrityHome = async () => {
      const token = getAccessToken();
      try {
        const response = await axios.get("/get-celebrity-home", {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        });
        setCelebrityHome(response.data);
      } catch (error) {
        console.error("Error fetching celebrityhomes:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCelebrityHome();
  }, []);

  // Function to auto-size columns based on the longest data entry
  const autoFitColumns = (jsonData) => {
    return Object.keys(jsonData[0] || {}).map((key) => ({
      wch:
        Math.max(
          key.length, // Minimum width should fit the header text
          ...jsonData.map((row) => (row[key] ? row[key].toString().length : 0)) // Longest cell
        ) + 5, // Add some extra padding
    }));
  };

  // Function to download Excel
  const handleDownloadExcel = () => {
    if (celebrityhomes.length === 0) {
      alert("No data available to download.");
      return;
    }
    const homeInformationData = celebrityhomes.map((celebrityhome, index) => ({
      "S.No": index + 1,
      "Celebrity Name":
        celebrityhome.homeInfo?.celebrityName?.join(", ") || "N/A",
      "Home Type": celebrityhome.homeInfo?.homeType || "N/A",
      "Total Floor Area (sq. ft.)":
        celebrityhome.homeInfo?.totalFloorArea || "N/A",
      "Total Floors": celebrityhome.homeInfo?.totalFloors || "N/A",
      "Total Rooms": celebrityhome.homeInfo?.totalRooms || "N/A",
      "Luxury Certifications":
        celebrityhome.homeInfo?.luxuryCertifications?.join(", ") || "N/A",
    }));

    const dataCollectionInformation = celebrityhomes.map(
      (celebrityhome, index) => ({
        "S.No": index + 1,
        "Celebrity Name":
          celebrityhome.homeInfo?.celebrityName?.join(", ") || "N/A",
        "Date of Data Collection": celebrityhome.dataCollection
          ?.dateOfDataCollection
          ? new Date(
              celebrityhome.dataCollection.dateOfDataCollection
            ).toLocaleDateString()
          : "N/A",
        "Data Collected By":
          celebrityhome.dataCollection?.dataCollectedBy?.join(", ") || "N/A",
        "Data Source": celebrityhome.dataCollection?.dataSource || "N/A",
      })
    );

    const homeLayoutInformation = celebrityhomes.map(
      (celebrityhome, index) => ({
        "S.No": index + 1,
        "Celebrity Name":
          celebrityhome.homeInfo?.celebrityName?.join(", ") || "N/A",
        Bedrooms: celebrityhome.homeLayout?.bedrooms || "N/A",
        Bathrooms: celebrityhome.homeLayout?.bathrooms || "N/A",
        "Guest Suites": celebrityhome.homeLayout?.guestSuites || "N/A",
        "Cinema Room": celebrityhome.homeLayout?.cinemaRoom ? "Yes" : "No",
        "Private Gym": celebrityhome.homeLayout?.privateGym ? "Yes" : "No",
        "Indoor Spa": celebrityhome.homeLayout?.indoorSpa ? "Yes" : "No",
        "Home Office": celebrityhome.homeLayout?.homeOffice ? "Yes" : "No",
        "Game Room": celebrityhome.homeLayout?.gameRoom ? "Yes" : "No",
        "Wine Cellar": celebrityhome.homeLayout?.wineCellar ? "Yes" : "No",
        "Walk-In Closets": celebrityhome.homeLayout?.walkInClosetCount || "N/A",
        "Outdoor Spaces": celebrityhome.homeLayout?.outdoorSpaces || "N/A",
      })
    );

    const cleaningDisinfectionData = celebrityhomes.map(
      (celebrityhome, index) => ({
        "S.No": index + 1,
        "Celebrity Name":
          celebrityhome.homeInfo?.celebrityName?.join(", ") || "N/A",
        "Total Budget (Per Month)":
          celebrityhome.cleaningDisinfect?.totalBudgetPerMonth || "N/A",
        "Cleaning Cost (Bedrooms %)":
          celebrityhome.cleaningDisinfect?.cleaningCostbedrooms || "N/A",
        "Cleaning Cost (Common Areas %)":
          celebrityhome.cleaningDisinfect?.cleaningCostcommonAreas || "N/A",
        "Cleaning Cost (Gym & Spa %)":
          celebrityhome.cleaningDisinfect?.cleaningCostgymSpa || "N/A",
        "Cleaning Cost (Entertainment Areas %)":
          celebrityhome.cleaningDisinfect?.cleaningCostentertainmentAreas ||
          "N/A",
        "Cleaning Cost (Kitchen %)":
          celebrityhome.cleaningDisinfect?.cleaningCostkitchen || "N/A",
        "Disinfection Methods":
          celebrityhome.cleaningDisinfect?.currentDisinfectionMethods?.join(
            ", "
          ) || "N/A",
        "Disinfection Frequency (Per Room/Day)":
          celebrityhome.cleaningDisinfect?.disinfectionFrequencyPerRoomPerDay ||
          "N/A",
        Challenges:
          celebrityhome.cleaningDisinfect?.challenges?.join(", ") || "N/A",
      })
    );

    const privacySecurityData = celebrityhomes.map((celebrityhome, index) => ({
      "S.No": index + 1,
      "Celebrity Name":
        celebrityhome.homeInfo?.celebrityName?.join(", ") || "N/A",
      "Privacy Concern Level":
        celebrityhome.privacySecurity?.privacyConcernLevel || "N/A",
      "Security Clearance Required": celebrityhome.privacySecurity
        ?.securityClearanceRequired
        ? "Yes"
        : "No",
      "Restricted Areas":
        celebrityhome.privacySecurity?.restrictedAreas?.join(", ") || "N/A",
      "AI Privacy Concerns": celebrityhome.privacySecurity
        ?.aiAutomationPrivacyConcerns
        ? "Yes"
        : "No",
      "Custom Security Needs":
        celebrityhome.privacySecurity?.customSecurityIntegrationNeeds?.join(
          ", "
        ) || "N/A",
    }));

    const staffAutomationData = celebrityhomes.map((celebrityhome, index) => ({
      "S.No": index + 1,
      "Celebrity Name":
        celebrityhome.homeInfo?.celebrityName?.join(", ") || "N/A",
      "No. of Housekeeping Staff":
        celebrityhome.staffAutomation?.numberOfHousekeepingStaff || "N/A",
      "Shift Hours":
        celebrityhome.staffAutomation?.cleaningStaffShiftHours?.join(", ") ||
        "N/A",
      "Service Type":
        celebrityhome.staffAutomation?.housekeepingServiceType || "N/A",
      "Automated Disinfection Interest": celebrityhome.staffAutomation
        ?.interestInAutomatedDisinfection
        ? "Yes"
        : "No",
      "Customization Needs (Scheduling)":
        celebrityhome.staffAutomation?.customizationNeedsForScheduling?.join(
          ", "
        ) || "N/A",
      "Smart Home Integration": celebrityhome.staffAutomation
        ?.integrationWithSmartHomeSystem
        ? "Yes"
        : "No",
      "Concerns About UV-C": celebrityhome.staffAutomation
        ?.concernsAboutUVCUsage
        ? "Yes"
        : "No",
    }));

    const smartHomeData = celebrityhomes.map((celebrityhome, index) => ({
      "S.No": index + 1,
      "Celebrity Name":
        celebrityhome.homeInfo?.celebrityName?.join(", ") || "N/A",
      "Existing Smart Home System": celebrityhome.smartHome
        ?.existingSmartHomeSystem
        ? "Yes"
        : "No",
      "Preferred Brands":
        celebrityhome.smartHome?.preferredBrands?.join(", ") || "N/A",
      "Integration Needs": celebrityhome.smartHome?.integrationNeeds || "N/A",
    }));

    const demoNextStepsData = celebrityhomes.map((celebrityhome, index) => ({
      "S.No": index + 1,
      "Celebrity Name":
        celebrityhome.homeInfo?.celebrityName?.join(", ") || "N/A",
      "Interest in On-Site Demo": celebrityhome.demoNextSteps
        ?.interestInOnSiteDemo
        ? "Yes"
        : "No",
      "Pilot Project Readiness": celebrityhome.demoNextSteps
        ?.pilotProjectReadiness
        ? "Yes"
        : "No",
      "Key Decision Maker":
        celebrityhome.demoNextSteps?.keyDecisionMaker || "N/A",
      "Expected Timeline":
        celebrityhome.demoNextSteps?.expectedTimelineForDecisionMaking?.join(
          ", "
        ) || "N/A",
    }));

    const additionalNotesData = celebrityhomes.map(
      (celebrityhome, index) => ({
        "S.No": index + 1,
        "Celebrity Name":
        celebrityhome.homeInfo?.celebrityName?.join(", ") || "N/A",
        "Additional Notes": celebrityhome.additionalnotes?.note || "N/A",
      })
    );
    const wb = XLSX.utils.book_new();

    // Convert JSON to Worksheet
    const ws1 = XLSX.utils.json_to_sheet(homeInformationData);
    const ws2 = XLSX.utils.json_to_sheet(dataCollectionInformation);
    const ws3 = XLSX.utils.json_to_sheet(homeLayoutInformation);
    const ws4 = XLSX.utils.json_to_sheet(cleaningDisinfectionData);
    const ws5 = XLSX.utils.json_to_sheet(privacySecurityData);
    const ws6 = XLSX.utils.json_to_sheet(staffAutomationData);
    const ws7 = XLSX.utils.json_to_sheet(smartHomeData);
    const ws8 = XLSX.utils.json_to_sheet(demoNextStepsData);
    const ws9 = XLSX.utils.json_to_sheet(additionalNotesData);

    // Auto-size columns based on content
    ws1["!cols"] = autoFitColumns(homeInformationData);
    ws2["!cols"] = autoFitColumns(dataCollectionInformation);
    ws3["!cols"] = autoFitColumns(homeLayoutInformation);
    ws4["!cols"] = autoFitColumns(cleaningDisinfectionData);
    ws5["!cols"] = autoFitColumns(privacySecurityData);
    ws6["!cols"] = autoFitColumns(staffAutomationData);
    ws7["!cols"] = autoFitColumns(smartHomeData);
    ws8["!cols"] = autoFitColumns(demoNextStepsData);
    ws9["!cols"] = autoFitColumns(additionalNotesData);

    // Set row height for better readability
    ws1["!rows"] = [{ hpx: 25 }];
    ws2["!rows"] = [{ hpx: 25 }];
    ws3["!rows"] = [{ hpx: 25 }];
    ws4["!rows"] = [{ hpx: 25 }];
    ws5["!rows"] = [{ hpx: 25 }];
    ws6["!rows"] = [{ hpx: 25 }];
    ws7["!rows"] = [{ hpx: 25 }];
    ws8["!rows"] = [{ hpx: 25 }];
    ws9["!rows"] = [{ hpx: 25 }];

    // Append sheets to workbook
    XLSX.utils.book_append_sheet(wb, ws1, "Home Info");
    XLSX.utils.book_append_sheet(wb, ws2, "Data Collection Info");
    XLSX.utils.book_append_sheet(wb, ws3, "Home Layout Info");
    XLSX.utils.book_append_sheet(wb, ws4, "Cleaning Disinfection Info");
    XLSX.utils.book_append_sheet(wb, ws5, "Privacy & Security Information");
    XLSX.utils.book_append_sheet(wb, ws6, "Staff Automation Info");
    XLSX.utils.book_append_sheet(wb, ws7, "Smart Home Info");
    XLSX.utils.book_append_sheet(wb, ws8, "Demo Next Steps Info");
    XLSX.utils.book_append_sheet(wb, ws9, "Additional Notes");

    // Get the current date
    const currentDate = new Date();
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, "0"); // Months are zero-based
    const day = String(currentDate.getDate()).padStart(2, "0");

    // Format the date as dd-mm-yyyy
    const formattedDate = `${day}-${month}-${year}`;

    // Create the filename with the current date
    const filename = `CelebrityHome${formattedDate}.xlsx`;

    // Write and download Excel file
    XLSX.writeFile(wb, filename);
  };

  const pageStyle = {
    position: "relative",
    minHeight: "100vh",
    background:
      "radial-gradient(1200px 600px at 10% 0%, rgba(37, 99, 235, 0.10), rgba(255,255,255,0) 60%), radial-gradient(900px 450px at 90% 15%, rgba(16, 185, 129, 0.10), rgba(255,255,255,0) 55%), #ffffff",
    fontFamily:
      "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    padding: "24px 32px 48px",
    boxSizing: "border-box",
  };

  if (loading) {
    return (
      <div
        style={{
          ...pageStyle,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 16,
        }}
      >
        <span style={{ color: "#64748b", fontSize: 15, fontWeight: 500 }}>
          Loading...
        </span>
      </div>
    );
  }
  return (
    <div style={pageStyle}>
      <IconButton
        onClick={() => navigate(-1)}
        style={{
          position: "absolute",
          top: 24,
          left: 24,
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          backdropFilter: "blur(10px)",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
          color: "#4a4a4a",
          zIndex: 10,
        }}
      >
        <ArrowBackIcon />
      </IconButton>

      <div style={{ textAlign: "center", marginTop: 20, marginBottom: 28 }}>
        <h1
          style={{
            margin: 0,
            marginBottom: 8,
            fontSize: "1.85rem",
            fontWeight: 700,
            letterSpacing: "-0.5px",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          Celebrity-Home Data
        </h1>
        <p
          style={{
            margin: 0,
            fontSize: "0.95rem",
            color: "#64748b",
            fontWeight: 400,
          }}
        >
          View and download celebrity home data
        </p>
      </div>

      <button style={downloadButton} onClick={handleDownloadExcel}>
        📥 Download Excel
      </button>

      {celebrityhomes.length === 0 ? (
        <div style={noDataStyle}>
          <h1>No Data Available</h1>
        </div>
      ) : (
        <>
          <h2 style={heading}>Home Information</h2>
          <div style={tableContainerStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>S.No</th>
                  <th style={thStyle}>Celebrity Name</th>
                  <th style={thStyle}>Home Type</th>
                  <th style={thStyle}>Total Floor Area (sq. ft.)</th>
                  <th style={thStyle}>Total Floors</th>
                  <th style={thStyle}>Total Rooms</th>
                  <th style={thStyle}>Luxury Certifications</th>
                </tr>
              </thead>
              <tbody>
                {celebrityhomes.map((celebrityhome, index) => (
                  <tr key={index}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={tdStyle}>
                      {celebrityhome.homeInfo.celebrityName?.join(", ") ||
                        "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {celebrityhome.homeInfo.homeType || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {celebrityhome.homeInfo.totalFloorArea || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {celebrityhome.homeInfo.totalFloors || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {celebrityhome.homeInfo.totalRooms || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {celebrityhome.homeInfo.luxuryCertifications?.join(
                        ", "
                      ) || "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 style={heading}>Data Collection Information</h2>
          <div style={tableContainerStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>S.No</th>
                  <th style={thStyle}>Celebrity Name</th>
                  <th style={thStyle}>Date of Data Collection</th>
                  <th style={thStyle}>Data Collected By</th>
                  <th style={thStyle}>Data Source</th>
                </tr>
              </thead>
              <tbody>
                {celebrityhomes.map((celebrityhome, index) => (
                  <tr key={index}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={tdStyle}>
                      {celebrityhome.homeInfo.celebrityName?.join(", ") ||
                        "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {celebrityhome.dataCollection.dateOfDataCollection
                        ? new Date(
                            celebrityhome.dataCollection.dateOfDataCollection
                          ).toLocaleDateString("en-GB")
                        : "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {celebrityhome.dataCollection.dataCollectedBy?.join(
                        ", "
                      ) || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {celebrityhome.dataCollection.dataSource || "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 style={heading}>Home Layout Information</h2>
          <div style={tableContainerStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>S.No</th>
                  <th style={thStyle}>Celebrity Name</th>
                  <th style={thStyle}>Bedrooms</th>
                  <th style={thStyle}>Bathrooms</th>
                  <th style={thStyle}>Guest Suites</th>
                  <th style={thStyle}>Cinema Room</th>
                  <th style={thStyle}>Private Gym</th>
                  <th style={thStyle}>Indoor Spa</th>
                  <th style={thStyle}>Home Office</th>
                  <th style={thStyle}>Game Room</th>
                  <th style={thStyle}>Wine Cellar</th>
                  <th style={thStyle}>Walk-In Closets</th>
                  <th style={thStyle}>Outdoor Spaces</th>
                </tr>
              </thead>
              <tbody>
                {celebrityhomes.map((celebrityhome, index) => (
                  <tr key={index}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={tdStyle}>
                      {celebrityhome.homeInfo.celebrityName?.join(", ") ||
                        "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {celebrityhome.homeLayout.bedrooms || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {celebrityhome.homeLayout.bathrooms || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {celebrityhome.homeLayout.guestSuites || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {celebrityhome.homeLayout.cinemaRoom ? "Yes" : "No"}
                    </td>
                    <td style={tdStyle}>
                      {celebrityhome.homeLayout.privateGym ? "Yes" : "No"}
                    </td>
                    <td style={tdStyle}>
                      {celebrityhome.homeLayout.indoorSpa ? "Yes" : "No"}
                    </td>
                    <td style={tdStyle}>
                      {celebrityhome.homeLayout.homeOffice ? "Yes" : "No"}
                    </td>
                    <td style={tdStyle}>
                      {celebrityhome.homeLayout.gameRoom ? "Yes" : "No"}
                    </td>
                    <td style={tdStyle}>
                      {celebrityhome.homeLayout.wineCellar ? "Yes" : "No"}
                    </td>
                    <td style={tdStyle}>
                      {celebrityhome.homeLayout.walkInClosetCount || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {celebrityhome.homeLayout.outdoorSpaces || "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 style={heading}>Cleaning & Disinfection Information</h2>
          <div style={tableContainerStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>S.No</th>
                  <th style={thStyle}>Celebrity Name</th>
                  <th style={thStyle}>Total Budget (Per Month)</th>
                  <th style={thStyle}>Cleaning Cost (Bedrooms %)</th>
                  <th style={thStyle}>Cleaning Cost (Common Areas %)</th>
                  <th style={thStyle}>Cleaning Cost (Gym & Spa %)</th>
                  <th style={thStyle}>Cleaning Cost (Entertainment Areas %)</th>
                  <th style={thStyle}>Cleaning Cost (Kitchen %)</th>
                  <th style={thStyle}>Disinfection Methods</th>
                  <th style={thStyle}>Disinfection Frequency (Per Room/Day)</th>
                  <th style={thStyle}>Challenges</th>
                </tr>
              </thead>
              <tbody>
                {celebrityhomes.map((celebrityhome, index) => (
                  <tr key={index}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={tdStyle}>
                      {celebrityhome.homeInfo.celebrityName?.join(", ") ||
                        "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {celebrityhome.cleaningDisinfect.totalBudgetPerMonth ||
                        "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {celebrityhome.cleaningDisinfect.cleaningCostbedrooms ||
                        "N/A"}
                      %
                    </td>
                    <td style={tdStyle}>
                      {celebrityhome.cleaningDisinfect
                        .cleaningCostcommonAreas || "N/A"}
                      %
                    </td>
                    <td style={tdStyle}>
                      {celebrityhome.cleaningDisinfect.cleaningCostgymSpa ||
                        "N/A"}
                      %
                    </td>
                    <td style={tdStyle}>
                      {celebrityhome.cleaningDisinfect
                        .cleaningCostentertainmentAreas || "N/A"}
                      %
                    </td>
                    <td style={tdStyle}>
                      {celebrityhome.cleaningDisinfect.cleaningCostkitchen ||
                        "N/A"}
                      %
                    </td>
                    <td style={tdStyle}>
                      {celebrityhome.cleaningDisinfect.currentDisinfectionMethods?.join(
                        ", "
                      ) || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {celebrityhome.cleaningDisinfect
                        .disinfectionFrequencyPerRoomPerDay || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {celebrityhome.cleaningDisinfect.challenges?.join(", ") ||
                        "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 style={heading}>Privacy & Security Information</h2>
          <div style={tableContainerStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>S.No</th>
                  <th style={thStyle}>Celebrity Name</th>
                  <th style={thStyle}>Privacy Concern Level</th>
                  <th style={thStyle}>Security Clearance Required</th>
                  <th style={thStyle}>Restricted Areas</th>
                  <th style={thStyle}>AI Privacy Concerns</th>
                  <th style={thStyle}>Custom Security Needs</th>
                </tr>
              </thead>
              <tbody>
                {celebrityhomes.map((celebrityhome, index) => (
                  <tr key={index}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={tdStyle}>
                      {celebrityhome.homeInfo.celebrityName?.join(", ") ||
                        "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {celebrityhome.privacySecurity.privacyConcernLevel ||
                        "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {celebrityhome.privacySecurity.securityClearanceRequired
                        ? "Yes"
                        : "No"}
                    </td>
                    <td style={tdStyle}>
                      {celebrityhome.privacySecurity.restrictedAreas?.join(
                        ", "
                      ) || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {celebrityhome.privacySecurity.aiAutomationPrivacyConcerns
                        ? "Yes"
                        : "No"}
                    </td>
                    <td style={tdStyle}>
                      {celebrityhome.privacySecurity.customSecurityIntegrationNeeds?.join(
                        ", "
                      ) || "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 style={heading}>Staff & Automation Information</h2>
          <div style={tableContainerStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>S.No</th>
                  <th style={thStyle}>Celebrity Name</th>
                  <th style={thStyle}>No. of Housekeeping Staff</th>
                  <th style={thStyle}>Shift Hours</th>
                  <th style={thStyle}>Service Type</th>
                  <th style={thStyle}>Automated Disinfection Interest</th>
                  <th style={thStyle}>Customization Needs (Scheduling)</th>
                  <th style={thStyle}>Smart Home Integration</th>
                  <th style={thStyle}>Concerns About UV-C</th>
                </tr>
              </thead>
              <tbody>
                {celebrityhomes.map((celebrityhome, index) => (
                  <tr key={index}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={tdStyle}>
                      {celebrityhome.homeInfo.celebrityName?.join(", ") ||
                        "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {celebrityhome.staffAutomation
                        .numberOfHousekeepingStaff || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {celebrityhome.staffAutomation.cleaningStaffShiftHours?.join(
                        ", "
                      ) || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {celebrityhome.staffAutomation.housekeepingServiceType ||
                        "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {celebrityhome.staffAutomation
                        .interestInAutomatedDisinfection
                        ? "Yes"
                        : "No"}
                    </td>
                    <td style={tdStyle}>
                      {celebrityhome.staffAutomation.customizationNeedsForScheduling?.join(
                        ", "
                      ) || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {celebrityhome.staffAutomation
                        .integrationWithSmartHomeSystem
                        ? "Yes"
                        : "No"}
                    </td>
                    <td style={tdStyle}>
                      {celebrityhome.staffAutomation.concernsAboutUVCUsage
                        ? "Yes"
                        : "No"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 style={heading}>Smart Home Information</h2>
          <div style={tableContainerStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>S.No</th>
                  <th style={thStyle}>Celebrity Name</th>
                  <th style={thStyle}>Existing Smart Home System</th>
                  <th style={thStyle}>Preferred Brands</th>
                  <th style={thStyle}>Integration Needs</th>
                </tr>
              </thead>
              <tbody>
                {celebrityhomes.map((celebrityhome, index) => (
                  <tr key={index}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={tdStyle}>
                      {celebrityhome.homeInfo.celebrityName?.join(", ") ||
                        "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {celebrityhome.smartHome.existingSmartHomeSystem
                        ? "Yes"
                        : "No"}
                    </td>
                    <td style={tdStyle}>
                      {celebrityhome.smartHome.preferredBrands?.join(", ") ||
                        "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {celebrityhome.smartHome.integrationNeeds || "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 style={heading}>Demo & Next Steps</h2>
          <div style={tableContainerStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>S.No</th>
                  <th style={thStyle}>Celebrity Name</th>
                  <th style={thStyle}>Interest in On-Site Demo</th>
                  <th style={thStyle}>Pilot Project Readiness</th>
                  <th style={thStyle}>Key Decision Maker</th>
                  <th style={thStyle}>Expected Timeline</th>
                </tr>
              </thead>
              <tbody>
                {celebrityhomes.map((celebrityhome, index) => (
                  <tr key={index}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={tdStyle}>
                      {celebrityhome.homeInfo.celebrityName?.join(", ") ||
                        "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {celebrityhome.demoNextSteps.interestInOnSiteDemo
                        ? "Yes"
                        : "No"}
                    </td>
                    <td style={tdStyle}>
                      {celebrityhome.demoNextSteps.pilotProjectReadiness
                        ? "Yes"
                        : "No"}
                    </td>
                    <td style={tdStyle}>
                      {celebrityhome.demoNextSteps.keyDecisionMaker || "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {celebrityhome.demoNextSteps.expectedTimelineForDecisionMaking?.join(
                        ", "
                      ) || "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 style={heading}>* Additional Notes *</h2>
          <div style={tableContainerStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>S.No</th>
                  <th style={thStyle}>Celebrity Name</th>
                  <th style={thStyle}>Notes</th>
                </tr>
              </thead>
              <tbody>
                {celebrityhomes.map((celebrityhome, index) => (
                  <tr key={index}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={tdStyle}>
                      {celebrityhome.homeInfo.celebrityName?.join(", ") ||
                        "N/A"}
                    </td>
                    <td style={tdStyle}>
                      {celebrityhome.additionalnotes.note || "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

// Internal CSS styles
const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  marginTop: "10px",
};

const thStyle = {
  backgroundColor: "#007bff",
  color: "#fff",
  padding: "10px",
  textAlign: "center",
  border: "1px solid #ddd",
};
const tableContainerStyle = {
  width: "100%",
  overflowX: "auto", // Enables horizontal scrolling
};
const tdStyle = {
  padding: "10px",
  border: "1px solid #ddd",
  textAlign: "center",
};

const heading = {
  fontSize: "24px",
  color: "black",
};

const downloadButton = {
  position: "absolute",
  top: "20px",
  right: "10px",
  backgroundColor: "#007bff",
  color: "white",
  border: "none",
  padding: "10px 15px",
  cursor: "pointer",
  borderRadius: "5px",
  fontSize: "14px",
};
const noDataStyle = {
  textAlign: "center",
  marginTop: "20px",
  fontSize: "20px",
  color: "#888",
};
export default CelebrityHomeDetails;
